import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { CopilotAPI, FileAPI } from "../../../../../api";
import UploadFileComponent from "../../../../../components/core/UploadFile/UploadFileComponent";
import { CopilotAutofillElement } from "../../../../../features/task/types/task-types/TaskForm";
import { useGlobalOrganizationContext } from "../../../../../hooks/useGlobalOrganizationContext";
import {
  FormExecutionType,
  TaskExecutionState,
  TaskFormExecution,
} from "../../../types";
import { ButtonGroupFunction } from "../../task-types/form/wizard-form-elements/TaskButtonGroupFormComponent";

type CopilotAutofillFormComponentProps = CopilotAutofillElement & {
  execution?: FormExecutionType;
  taskExecution?: TaskFormExecution;
  onAutofillComplete?: (
    data: Record<string, unknown>,
    assignment?: {
      action?: ButtonGroupFunction;
      data?: number;
    },
  ) => void;
  disabled?: boolean;
};

export const CopilotAutofillFormComponent = ({
  ...props
}: CopilotAutofillFormComponentProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>();
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<object | null>(null);
  const organization = useGlobalOrganizationContext().organization;

  const loadingMessages = [
    "Copilot is working...",
    "Extracting relevant information...",
    "Almost done...",
    "This is taking longer than expected...\n We're almost there!",
  ];
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);

  // Cycle loading message every 8 seconds, stop at last message
  useEffect(() => {
    if (!loading) return;
    setLoadingMsgIdx(0); // Reset to first message when loading starts
    const interval = setInterval(() => {
      setLoadingMsgIdx((idx) =>
        idx < loadingMessages.length - 1 ? idx + 1 : idx,
      );
    }, 9000);
    return () => clearInterval(interval);
  }, [loading, loadingMessages.length]);

  const handleCompletion = (res: Record<string, unknown>) => {
    setResult(res);
    props.onAutofillComplete?.(res, {
      action: props.onExecute,
      data: props.data,
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (!e.target.files || e.target.files.length === 0) {
      setError("No file selected. Please choose a file to upload.");
      return;
    }
    const file = e.target.files[0];
    setFiles([file]);
    setLoading(true);
    try {
      const uploadRes = await FileAPI.uploadFile(e.target.files);
      if (!uploadRes || !uploadRes[0]?.key) {
        setError(
          "File upload failed. Please try again or use a different file.",
        );
        setLoading(false);
        return;
      }
      const fileKey = uploadRes[0].key;
      const taskExecutionId = props.taskExecution?.id ?? "";
      const res = await CopilotAPI.autofill(
        taskExecutionId,
        fileKey,
        props.bias,
        organization?.id,
      );
      if (!res) {
        setError(
          "Copilot did not return any results. Please check your file and try again.",
        );
        setLoading(false);
        return;
      }
      handleCompletion(res);
    } catch (e) {
      setError(
        `Failed to process file, please make sure it is a compatible file type!`,
      );
      console.error(e);
    }
    setLoading(false);
  };

  const handleDrop = async (e: React.DragEvent<Element>) => {
    e.preventDefault();
    setError(null);
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
      setError("No file detected in drop event. Please drag a valid file.");
      return;
    }
    const file = e.dataTransfer.files[0];
    setFiles([file]);
    setLoading(true);
    try {
      const uploadRes = await FileAPI.uploadFile(e.dataTransfer.files);
      if (!uploadRes || !uploadRes[0]?.key) {
        setError(
          "File upload failed. Please try again or use a different file.",
        );
        setLoading(false);
        return;
      }
      const fileKey = uploadRes[0].key;
      const taskExecutionId = props.taskExecution?.id ?? "";
      const res = await CopilotAPI.autofill(
        taskExecutionId,
        fileKey,
        props.bias,
        organization?.id,
      );
      if (!res) {
        setError(
          "Copilot did not return any results. Please check your file and try again.",
        );
        setLoading(false);
        return;
      }
      handleCompletion(res);
    } catch (e) {
      setError(`Failed to process file: ${e || "Unknown error occurred."}`);
      console.error(e);
    }
    setLoading(false);
  };

  const holderStyle = {
    position: "relative",
    py: files.length > 0 ? 2 : 3,
    px: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    border: "1px dashed rgba(0, 0, 0, 0.12)",
    borderRadius: "8px",
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    transition: "all 0.2s ease",
  };

  if (props.taskExecution?.state == TaskExecutionState.Completed)
    return (
      <Typography variant="subtitle1">
        {`Process completed with Copilot Autofill.`}
      </Typography>
    );

  return (
    <Box sx={{ width: "100%" }}>
      {!loading && !error && !result && (
        <>
          <Typography variant="body2">{props.label}</Typography>
          <UploadFileComponent
            files={files}
            handleDrop={handleDrop}
            handleFileChange={handleFileChange}
            multiple={false}
            accept={props.fileTypes}
            text={
              props.disabled
                ? "Copilot File Upload"
                : "Drag and drop a file here"
            }
            uploadText="Select file"
            renderer={() => undefined}
            onRemove={() => setFiles([])}
            disabled={loading || props.disabled}
          />
        </>
      )}

      {loading && (
        <Box
          sx={{
            ...holderStyle,
            border: "1px solid rgba(0, 0, 0, 0.12)",
            mt: 2,
            pt: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
          <Typography variant="overline" sx={{ ml: 2 }}>
            {loadingMessages[loadingMsgIdx]}
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={(t) => ({
            ...holderStyle,
            backgroundColor: t.palette.error.light,
            border: "1px solid " + t.palette.error.dark,
            color: t.palette.success.contrastText,
            boxShadow: 2,
            mt: 2,
            minHeight: 80,
            justifyContent: "center",
          })}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Autofill Unsuccessful!
          </Typography>
          <Typography>
            {
              "We were unable to process this file. We apologize for the inconvenience."
            }
          </Typography>
          <Typography variant="caption">{error}</Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setFiles([]);
                setError(null);
              }}
              sx={{
                color: (t) => t.palette.error.contrastText,
                borderColor: (t) => t.palette.error.contrastText,
                "&:hover": {
                  borderColor: (t) => t.palette.error.contrastText,
                  backgroundColor: "transparent",
                },
              }}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      )}

      {result && (
        <Box
          sx={(t) => ({
            ...holderStyle,
            backgroundColor: t.palette.success.light,
            border: "1px solid " + t.palette.success.dark,
            color: t.palette.success.contrastText,
            boxShadow: 2,
            mt: 2,
            minHeight: 80,
            justifyContent: "center",
          })}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Autofill Complete!
          </Typography>
          <Typography
            variant="body2"
            sx={(t) => ({
              mt: 0.5,
              color: t.palette.success.contrastText,
              textAlign: "center",
            })}
          >
            {`Your form has been autofilled. You can review and edit the results
            if you'd like.`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
